/**
 * Avatar Utilities
 * Funções auxiliares para o componente Avatar
 */

// Tamanho padrão do avatar armazenado
export const AVATAR_STORAGE_SIZE = 256;

// Paleta de cores harmonizadas com a cor primária Joshua (#154680)
const AVATAR_COLORS = [
  '#154680', // Joshua primary
  '#1e5a9e', // Blue variant
  '#2d6a4f', // Green
  '#7c3aed', // Purple
  '#dc2626', // Red
  '#ea580c', // Orange
  '#0891b2', // Cyan
  '#4f46e5', // Indigo
  '#059669', // Emerald
  '#d97706', // Amber
];

export interface AvatarCropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AvatarFileValidation {
  valid: boolean;
  error?: string;
}

/**
 * Gera as iniciais a partir do nome
 * @param {string} name - Nome completo
 * @returns {string} - Iniciais (máximo 2 caracteres)
 */
export function getInitials(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return '';

  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  // Primeira e última inicial
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Gera uma cor deterministicamente baseada no nome
 * @param {string} name - Nome para gerar cor
 * @returns {string} - Cor hexadecimal
 */
export function getColorFromName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return AVATAR_COLORS[0];

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

/**
 * Redimensiona uma imagem para o tamanho do avatar
 * @param {File} file - Arquivo de imagem
 * @param {Object} cropData - Dados do crop { x, y, width, height }
 * @param {number} size - Tamanho final em pixels
 * @returns {Promise<Blob>} - Blob da imagem redimensionada
 */
export async function resizeImage(
  file: File,
  cropData: AvatarCropData | null | undefined,
  size: number = AVATAR_STORAGE_SIZE
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = size;
        canvas.height = size;

        if (!ctx) {
          reject(new Error('Falha ao obter contexto 2D do canvas'));
          return;
        }

        // Se temos dados de crop, usar eles
        if (cropData && cropData.width && cropData.height) {
          ctx.drawImage(
            img,
            cropData.x,
            cropData.y,
            cropData.width,
            cropData.height,
            0,
            0,
            size,
            size
          );
        } else {
          // Crop centralizado (quadrado)
          const minDimension = Math.min(img.width, img.height);
          const sx = (img.width - minDimension) / 2;
          const sy = (img.height - minDimension) / 2;

          ctx.drawImage(
            img,
            sx,
            sy,
            minDimension,
            minDimension,
            0,
            0,
            size,
            size
          );
        }

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Falha ao criar blob da imagem'));
            }
          },
          'image/jpeg',
          0.9 // 90% quality
        );
      };

      img.onerror = () => reject(new Error('Falha ao carregar imagem'));
      const result = e.target?.result;
      if (typeof result !== 'string') {
        reject(new Error('Falha ao ler arquivo'));
        return;
      }
      img.src = result;
    };

    reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
    reader.readAsDataURL(file);
  });
}

/**
 * Cria a área de crop a partir dos dados do react-easy-crop
 * @param {string} imageSrc - URL da imagem
 * @param {Object} pixelCrop - Dados do crop em pixels { x, y, width, height }
 * @returns {Promise<Blob>} - Blob da imagem cropada
 */
export async function getCroppedImage(
  imageSrc: string,
  pixelCrop: AvatarCropData
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = AVATAR_STORAGE_SIZE;
      canvas.height = AVATAR_STORAGE_SIZE;

      if (!ctx) {
        reject(new Error('Falha ao obter contexto 2D do canvas'));
        return;
      }

      ctx.drawImage(
        img,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        AVATAR_STORAGE_SIZE,
        AVATAR_STORAGE_SIZE
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Falha ao criar blob da imagem'));
          }
        },
        'image/jpeg',
        0.9
      );
    };

    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = imageSrc;
  });
}

/**
 * Converte File para base64 data URL
 * @param {File} file - Arquivo
 * @returns {Promise<string>} - Data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Falha ao ler arquivo'));
        return;
      }
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Valida se o arquivo é uma imagem válida para avatar
 * @param {File} file - Arquivo a validar
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateAvatarFile(file: File | null | undefined): AvatarFileValidation {
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  if (!file) {
    return { valid: false, error: 'Nenhum arquivo selecionado' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Formato não suportado. Use JPG, PNG, GIF ou WebP' };
  }

  if (file.size > MAX_SIZE) {
    return { valid: false, error: 'Arquivo muito grande. Máximo 5MB' };
  }

  return { valid: true };
}
